import { ExamEventCourseStatus, type RegistrationType } from '@exam-center/graphql';
import dayjs from 'dayjs';

import {
  type ExamDistributionDataRowItem,
  type ExamDistributionRoundTime,
  type RegistrationType as LabelRegistrationType,
} from '../../components';
import {
  type ExamDistributionEvent,
  type ExamDistributionEventCourse,
  type ExamDistributionCourse,
} from '../../types';

const REGISTRATION_TYPE_MAP: Record<RegistrationType, LabelRegistrationType> = {
  IN_HOUSE: 'IH',
  PUBLIC: 'PUB',
};

export function getUniqueExamCoursesFromExamEventCourses(
  examCourses: ExamDistributionCourse[],
  examEventCourses: ExamDistributionEventCourse[],
) {
  const uniqueExamCourseIdsInEvent = new Set(
    examEventCourses.map((examEventCourse) => examEventCourse.examCourseId),
  );

  return examCourses.filter((examCourse) => uniqueExamCourseIdsInEvent.has(examCourse.id));
}

export function mapExamCourseHeaders(examCourses: ExamDistributionCourse[]) {
  return [...examCourses]
    .sort((a, b) => {
      return a.examId - b.examId;
    })
    .map((examCourse) => examCourse.code);
}

export function mapExamDistributionData(
  examEvents: ExamDistributionEvent[],
  examCourses: ExamDistributionCourse[],
  examEventCourses: ExamDistributionEventCourse[],
  canUpdate: boolean,
) {
  const examDistributionData: ExamDistributionDataRowItem<readonly string[]>[] = [];

  for (const examEvent of examEvents) {
    const { examStartedAt, examEndedAt } = examEvent;

    // STEP 1: group by day & date first
    const day = dayjs(examStartedAt).locale('en').format('ddd');
    const date = examStartedAt.getDate().toString();
    const startTime = getExamEventTimeAsString(examStartedAt);
    const endTime = getExamEventTimeAsString(examEndedAt);
    const examDistributionDateIdx = examDistributionData.findIndex(
      (data) => data.day === day && data.date === date,
    );

    if (examDistributionDateIdx !== -1) {
      // If current day & date is already existed

      // STEP 2: group by exam time
      const examDistributionTimeIdx = examDistributionData[
        examDistributionDateIdx
      ].rounds.findIndex((round) => round.time.from === startTime && round.time.to === endTime);

      if (examDistributionTimeIdx !== -1) {
        // If current exam time is already existed

        // STEP 3: spread exam info
        examDistributionData[examDistributionDateIdx].rounds[examDistributionTimeIdx].info.push(
          createExamInfo(examEvent, examCourses, examEventCourses, canUpdate),
        );
        examDistributionData[examDistributionDateIdx].rounds[examDistributionTimeIdx].info.sort(
          (a, b) => {
            // Primary sort: examCenterId
            if (a.examCenterId !== b.examCenterId) {
              return a.examCenterId - b.examCenterId;
            }
            // Secondary sort: roundNumber
            if (a.roundNumber !== b.roundNumber) {
              return a.roundNumber - b.roundNumber;
            }
            return 0;
          },
        );
      } else {
        // If current exam time is not existed, add new
        examDistributionData[examDistributionDateIdx].rounds.push({
          time: createTimeInfo(startTime, endTime),
          info: [createExamInfo(examEvent, examCourses, examEventCourses, canUpdate)],
        });
        examDistributionData[examDistributionDateIdx].rounds.sort((a, b) =>
          compareTimeRanges(a.time, b.time),
        );
      }
    } else {
      // If current day & date is not existed, add new
      examDistributionData.push({
        day,
        date,
        rounds: [
          {
            time: createTimeInfo(startTime, endTime),
            info: [createExamInfo(examEvent, examCourses, examEventCourses, canUpdate)],
          },
        ],
      });
      examDistributionData.sort((a, b) => parseInt(a.date) - parseInt(b.date));
    }
  }

  return examDistributionData;
}

function getExamEventTimeAsString(date: Date) {
  return date
    .toLocaleTimeString('th', {
      hour: 'numeric',
      minute: '2-digit',
    })
    .toString();
}

function createTimeInfo(startTime: string, endTime: string) {
  return {
    from: startTime,
    to: endTime,
  };
}

function createExamInfo(
  examEvent: ExamDistributionEvent,
  examCourses: ExamDistributionCourse[],
  examEventCourses: ExamDistributionEventCourse[],
  canUpdate: boolean,
) {
  return {
    examCenterId: examEvent.examCenter.examCenterId,
    examEventId: examEvent.id,
    examCenterCode: examEvent.examCenterCode,
    roundNumber: examEvent.roundNumber,
    examDate: examEvent.examStartedAt,
    examEventStatus: examEvent.status,
    examType: examEvent.examType,
    lastDownloadedAt: examEvent.metadata.lastDownloadedAt,
    location: examEvent.examLocation.nameTh,
    registrationType: REGISTRATION_TYPE_MAP[examEvent.registrationType],
    canUpdate,
    examEventCourses: examCourses.map((examCourse) => {
      const examEventCourse = examEventCourses.find(
        (examEventCourse) =>
          examEventCourse.examEventId === examEvent.id &&
          examEventCourse.examCourseId === examCourse.id,
      );
      return {
        course: examCourse.code,
        qSetCode: examEventCourse?.qSet?.code?.split('-')?.pop(),
        status: examEventCourse?.status ?? ExamEventCourseStatus.NoAccess,
      };
    }),
  };
}

function compareTimeRanges(a: ExamDistributionRoundTime, b: ExamDistributionRoundTime) {
  const fromA = toMinutes(a.from);
  const fromB = toMinutes(b.from);

  if (fromA !== fromB) return fromA - fromB;

  const toA = toMinutes(a.to);
  const toB = toMinutes(b.to);

  return toA - toB;
}

function toMinutes(time: string) {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}
